import React, { useState, useEffect } from "react";
import { GetRolesApi } from "../../../service/repos/roles_repo";
import {
  GetDepartments,
  GetUsersByRoleAndDepartment,
  DeleteRolesByUser
} from "../../../service/repos/users_repo";
import { Role } from "../../../context_providers/auth/user_model";
import {
  useToast,
  ToastType,
} from "../../../context_providers/toast/toast_ctx";
import DeleteRoleModal from "./components/RoleModel";
import { RoleModel } from "../content_panel/infrastructure/models/user_model";

const UserRoleScreen = () => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);
  interface Department {
    department: string;
    subDepartments: Array<{
      subDepartment: string;
      count: number;
    }>;
    count: number;
    created_at: string;
  }

  const [departments, setDepartments] = useState<Department[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<string>("");
  const [selectedSubDepartment, setSelectedSubDepartment] =
    useState<string>("");
  const [users, setUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalCount: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [roleSearchTerm, setRoleSearchTerm] = useState<string>("");
  const [userSearchTerm, setUserSearchTerm] = useState<string>("");
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [userToDelete, setUserToDelete] = useState<any>(null);
  const [rolesToDelete, setRolesToDelete] = useState<string[]>([]);
  const handleDeleteClick = (user: any) => {
    setUserToDelete(user);
    setRolesToDelete([...selectedRoles]); // selectedRoles is already string[]
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete || rolesToDelete.length === 0) return;

    try {
      setLoading(true);
      const result = await DeleteRolesByUser(
        userToDelete.email,
        rolesToDelete
      );

      if (result.success) {
        showToast({ type: ToastType.success, text: result.message });
        setShowDeleteModal(false);
        setUserToDelete(null);
        setRolesToDelete([]);
        // Refresh the users list
        fetchUsers(currentPage);
      } else {
        showToast({ type: ToastType.error, text: result.error });
      }
    } catch (error: any) {
      showToast({ type: ToastType.error, text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const getAllRoles = async () => {
    try {
      setLoading(true);
      const res = await GetRolesApi({ all: true });

      if (res.success && res.data?.data) {
        // Filter out deleted roles and sort by title
        const activeRoles = res.data.data
          .filter((role: Role) => !role.deleted)
          .sort((a: Role, b: Role) => a.title.localeCompare(b.title));
        setRoles(activeRoles);
      } else {
        showToast({
          type: ToastType.error,
          text: res.error ?? "Failed to fetch roles",
        });
      }
    } catch (err: any) {
      showToast({
        type: ToastType.error,
        text: err.message ?? "Failed to fetch roles",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async (page: number = 1) => {
    if (selectedRoles.length === 0) {
      setUsers([]);
      setPagination({
        currentPage: 1,
        totalPages: 0,
        totalCount: 0,
        hasNextPage: false,
        hasPrevPage: false,
      });
      return;
    }

    try {
      setLoadingUsers(true);
      const res = await GetUsersByRoleAndDepartment(
        selectedRoles,
        selectedDepartment || undefined,
        selectedSubDepartment || undefined,
        page,
        10, // limit per page
        userSearchTerm || undefined
      );
      if (res.success) {
        setUsers(res.data || []);
        if (res.pagination) {
          setPagination(res.pagination);
        }
      } else {
        showToast({
          type: ToastType.error,
          text: res.error ?? "Failed to fetch users",
        });
      }
    } catch (err: any) {
      showToast({
        type: ToastType.error,
        text: err.message ?? "Failed to fetch users",
      });
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    fetchUsers(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedRoles,
    selectedDepartment,
    selectedSubDepartment,
    userSearchTerm,
  ]);

  // Effect to fetch users when page changes
  useEffect(() => {
    if (selectedRoles.length > 0) {
      fetchUsers(currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
    }
  };

  const getAllDepartments = async () => {
    try {
      setLoading(true);
      const res = await GetDepartments();
      if (res.success) {
        // Sort departments alphabetically
        const sortedDepartments = [...res.data].sort((a, b) =>
          a.department.localeCompare(b.department)
        );
        setDepartments(sortedDepartments);
      } else {
        showToast({
          type: ToastType.error,
          text: res.error ?? "Failed to fetch departments",
        });
      }
    } catch (err: any) {
      showToast({
        type: ToastType.error,
        text: err.message ?? "Failed to fetch departments",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Fetch both roles and departments when component mounts
    const fetchData = async () => {
      try {
        setLoading(true);
        await Promise.all([getAllRoles(), getAllDepartments()]);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);
  return (
    <div className="p-2 md:p-5 h-screen">
      <div className="flex">
        <h1 className="text-xl font-bold">Role - User Screen</h1>
      </div>
      <div className="flex  border rounded items-center justify-between bg-white p-5">
        <div className="w-full space-y-4">
          {/* Filter Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="flex flex-col">
              <label htmlFor="role" className="font-semibold text-sm mb-2">
                Role
              </label>
              <div id="user_roles_role_filter" className="border rounded p-2 max-h-40 overflow-y-auto ">
                <input
                  id="user_roles_role_search"
                  type="text"
                  placeholder="Search roles..."
                  value={roleSearchTerm}
                  onChange={(e) => setRoleSearchTerm(e.target.value)}
                  className="w-full px-2 py-1.5 border border-gray-300 rounded mb-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {roles
                  .filter((role: any) =>
                    role.title
                      .toLowerCase()
                      .includes(roleSearchTerm.toLowerCase())
                  )
                  .map((role: any) => (
                    <div
                      key={role.id}
                      className="flex items-center gap-2 py-1 hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        id={`role-${role.id}`}
                        value={role.id}
                        checked={selectedRoles.includes(role.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedRoles([...selectedRoles, role.id]);
                          } else {
                            setSelectedRoles(
                              selectedRoles.filter((id) => id !== role.id)
                            );
                          }
                        }}
                        className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <label
                        htmlFor={`role-${role.id}`}
                        className="text-sm text-gray-700 cursor-pointer"
                      >
                        {role.title}
                      </label>
                    </div>
                  ))}
              </div>
              {selectedRoles.length > 0 && (
                <div className="mt-2">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">Selected Roles:</div>
                    <button
                      id="user_roles_clear_roles_btn"
                      onClick={() => setSelectedRoles([])}
                      className="text-xs text-red-600 hover:text-red-800"
                    >
                      Clear All
                    </button>
                  </div>
                  <div className="mt-1 grid grid-cols-3 gap-2">
                    {selectedRoles.map((roleId) => {
                      const role = roles.find((r) => r.id === roleId);
                      return (
                        <div
                          key={roleId}
                          className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full flex items-center justify-between"
                        >
                          <span className="truncate">{role?.title}</span>
                          <button
                            id={`user_roles_selected_role_${roleId}_remove_btn`}
                            onClick={() =>
                              setSelectedRoles(
                                selectedRoles.filter((id) => id !== roleId)
                              )
                            }
                            className="hover:text-blue-900 ml-1"
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="flex flex-col">
              <label
                htmlFor="user_roles_department_filter"
                className="font-semibold text-sm mb-2"
              >
                Department
              </label>
              <select
                id="user_roles_department_filter"
                className="border rounded p-2 w-full"
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                disabled={loading}
              >
                <option value="" hidden>
                  Select Department...
                </option>
                {departments.map((dept) => (
                  <option key={dept.department} value={dept.department}>
                    {dept.department}
                  </option>
                ))}
              </select>
              {selectedDepartment && (
                <div className="mt-2">
                  <div className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full inline-flex items-center gap-1">
                    <span>{selectedDepartment}</span>
                    <button
                      id="user_roles_department_clear_btn"
                      onClick={() => {
                        setSelectedDepartment("");
                        setSelectedSubDepartment("");
                      }}
                      className="hover:text-blue-900"
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="flex flex-col">
              <label
                htmlFor="user_roles_sub_department_filter"
                className="font-semibold text-sm mb-2"
              >
                Sub-Department (Please select department first)
              </label>
              <select
                id="user_roles_sub_department_filter"
                className="border rounded p-2 w-full"
                value={selectedSubDepartment}
                onChange={(e) => setSelectedSubDepartment(e.target.value)}
                disabled={!selectedDepartment || loading}
              >
                <option value="" hidden>
                  Select Sub Department...
                </option>
                {selectedDepartment &&
                  departments
                    .find((dept) => dept.department === selectedDepartment)
                    ?.subDepartments.sort((a, b) =>
                      a.subDepartment.localeCompare(b.subDepartment)
                    )
                    .map((subDept) => (
                      <option
                        key={subDept.subDepartment}
                        value={subDept.subDepartment}
                      >
                        {subDept.subDepartment}
                      </option>
                    ))}
              </select>
              {selectedSubDepartment && (
                <div className="mt-2">
                  <div className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full inline-flex items-center gap-1">
                    <span>{selectedSubDepartment}</span>
                    <button
                      id="user_roles_sub_department_clear_btn"
                      onClick={() => setSelectedSubDepartment("")}
                      className="hover:text-blue-900"
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Search Bar - Separate Row */}
          <div className="flex flex-col">
            <label className="font-semibold text-sm mb-2">Search Users</label>
            <div className="relative max-w-md">
              <input
                id="user_roles_user_search"
                type="text"
                placeholder="Search by name or email..."
                value={userSearchTerm}
                onChange={(e) => setUserSearchTerm(e.target.value)}
                className="w-full px-4 py-2 pl-10 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <svg
                className="absolute left-3 top-2.5 w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              {userSearchTerm && (
                <button
                  id="user_roles_user_search_clear_btn"
                  onClick={() => setUserSearchTerm("")}
                  className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex border rounded items-center justify-between bg-white p-5 mt-8">
        <div className="w-full">
          {selectedRoles.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              Select Role to view users
            </div>
          ) : loadingUsers ? (
            <div className="text-center text-gray-500 py-8">
              Loading users...
            </div>
          ) : users.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              No users found for the selected role
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Users List</h2>
              </div>

              {users.length > 0 && (
                <div className="mb-3 text-sm text-gray-600">
                  Showing {users.length} of {users.length} users
                  {userSearchTerm && ` matching "${userSearchTerm}"`}
                </div>
              )}

              {pagination.totalPages > 0 && (
                <div id="user_roles_pagination" className="mt-4 flex items-center justify-between px-4 py-3 border-t border-gray-200">
                  <div className="flex items-center text-sm text-gray-700">
                    <span>
                      Showing{" "}
                      <span className="font-medium">
                        {pagination.totalCount === 0
                          ? 0
                          : (pagination.currentPage - 1) * 10 + 1}
                      </span>{" "}
                      to{" "}
                      <span className="font-medium">
                        {Math.min(
                          pagination.currentPage * 10,
                          pagination.totalCount
                        )}
                      </span>{" "}
                      of{" "}
                      <span className="font-medium">
                        {pagination.totalCount}
                      </span>{" "}
                      results
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      id="user_roles_prev_page_btn"
                      onClick={() =>
                        handlePageChange(pagination.currentPage - 1)
                      }
                      disabled={!pagination.hasPrevPage || loadingUsers}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        !pagination.hasPrevPage || loadingUsers
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      Previous
                    </button>
                    <div className="flex items-center space-x-1">
                      {Array.from(
                        { length: pagination.totalPages },
                        (_, i) => i + 1
                      )
                        .filter((page) => {
                          // Show first page, last page, current page, and pages around current
                          return (
                            page === 1 ||
                            page === pagination.totalPages ||
                            Math.abs(page - pagination.currentPage) <= 1
                          );
                        })
                        .map((page, index, array) => {
                          // Add ellipsis if there's a gap
                          const showEllipsisBefore =
                            index > 0 && page - array[index - 1] > 1;
                          return (
                            <React.Fragment key={page}>
                              {showEllipsisBefore && (
                                <span className="px-3 py-2 text-sm text-gray-700">
                                  ...
                                </span>
                              )}
                              <button
                                id={`user_roles_page_${page}_btn`}
                                onClick={() => handlePageChange(page)}
                                disabled={loadingUsers}
                                className={`px-3 py-2 text-sm font-medium rounded-md ${
                                  pagination.currentPage === page
                                    ? "bg-blue-600 text-white"
                                    : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                                } ${loadingUsers ? "cursor-not-allowed" : ""}`}
                              >
                                {page}
                              </button>
                            </React.Fragment>
                          );
                        })}
                    </div>
                    <button
                      id="user_roles_next_page_btn"
                      onClick={() =>
                        handlePageChange(pagination.currentPage + 1)
                      }
                      disabled={!pagination.hasNextPage || loadingUsers}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        !pagination.hasNextPage || loadingUsers
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
              <div id="user_roles_users_table" className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Full Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Department
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Sub Department
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Mobile
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users.map((user: any) => (
                      <tr
                        id={`user_roles_row_${user.id || user._id}`}
                        key={user.id || user._id}
                        className="hover:bg-gray-50"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {user.first_name} {user.last_name}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {user.email}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {user.emp_profile?.department || "-"}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {user.emp_profile?.sub_dep || "-"}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {user.mobile || "-"}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            id={`user_roles_row_${user.id || user._id}_delete_roles_btn`}
                            onClick={() => handleDeleteClick(user)}
                            className="text-red-600 hover:text-red-800 text-sm font-medium px-3 py-1 border border-red-300 rounded hover:bg-red-50 transition-colors"
                          >
                            Delete Roles
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination Controls */}
            </div>
          )}
        </div>
      </div>

      <DeleteRoleModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setUserToDelete(null);
          setRolesToDelete([]);
        }}
        user={userToDelete}
        selectedRoles={selectedRoles}
        roles={roles.map((role) => ({
          id: role.id,
          name: role.title,
        }))}
        rolesToDelete={rolesToDelete}
        onRoleToggle={(roleId: string) => {
          setRolesToDelete((prev) =>
            prev.includes(roleId)
              ? prev.filter((id) => id !== roleId)
              : [...prev, roleId]
          );
        }}
        onConfirmDelete={handleConfirmDelete}
        loading={loading}
      />
    </div>
  );
};

export default UserRoleScreen;
